import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const EMPTY = { name: "", phone: "", email: "", city: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const LeadForm = () => {
  const { t, lang } = useLang();
  const f = t.form;
  const [data, setData] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const nameRef = useRef(null);

  // Address found in the coverage-map search is passed here via a window event
  useEffect(() => {
    const onPrefill = (e) => {
      const city = String(e.detail?.city ?? "").slice(0, 120);
      if (!city) return;
      setStatus("idle");
      setData((d) => ({ ...d, city }));
      setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 900);
    };
    window.addEventListener("lead-prefill", onPrefill);
    return () => window.removeEventListener("lead-prefill", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || data.phone.trim().length < 5) {
      toast.error(f.required);
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { ...data, lang });
      setStatus("done");
      setData(EMPTY);
      toast.success(f.ok);
    } catch {
      setStatus("idle");
      toast.error(f.err);
    }
  };

  if (status === "done") {
    return (
      <div className="h-full min-h-[420px] flex flex-col items-start justify-center gap-5" data-testid="lead-form-success">
        <CheckCircle2 className="w-14 h-14 text-[var(--gold)]" />
        <div className="font-display text-3xl text-[var(--text)]">{f.okTitle}</div>
        <p className="text-[var(--muted)] max-w-sm">{f.ok}</p>
        <button data-testid="lead-form-again-button" onClick={() => setStatus("idle")} className="btn-ghost mt-2">{f.again}</button>
      </div>
    );
  }

  const input = "mt-2 w-full bg-transparent border-b border-white/20 py-3 text-[var(--text)] placeholder:text-[var(--muted)]/70 outline-none focus:border-[var(--gold)] transition-colors";
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="lead-form">
      <Field label={f.name}><input ref={nameRef} data-testid="lead-name-input" className={input} value={data.name} onChange={set("name")} maxLength={120} /></Field>
      <Field label={f.phone}><input data-testid="lead-phone-input" className={input} type="tel" value={data.phone} onChange={set("phone")} maxLength={40} /></Field>
      <Field label={f.email}><input data-testid="lead-email-input" className={input} type="email" value={data.email} onChange={set("email")} maxLength={120} /></Field>
      <Field label={f.city}><input data-testid="lead-city-input" className={input} value={data.city} onChange={set("city")} maxLength={120} /></Field>
      <div className="sm:col-span-2">
        <Field label={f.message}><textarea data-testid="lead-message-input" rows={3} className={`${input} resize-none`} value={data.message} onChange={set("message")} maxLength={2000} /></Field>
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <p className="text-xs text-[var(--muted)] max-w-sm">{f.consent}</p>
        <button data-testid="lead-form-submit-button" type="submit" disabled={status === "sending"} className="btn-gold justify-center disabled:opacity-70">
          {status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> {f.sending}</> : <>{f.submit} <ArrowUpRight className="w-4 h-4" /></>}
        </button>
      </div>
    </form>
  );
};
